'use client';

import React, { useCallback, useState } from 'react';
import { AnimatePresence } from 'motion/react';

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@repo/ui/components/ui/drawer';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@repo/ui/components/ui/sheet';
import { useIsMobile } from '@repo/ui/hooks/use-mobile';

import { isArticleAnalysisPath } from '@/lib/analysis-paths';
import {
  createInlineModalRegistry,
  useAnalysisModalRoute,
  type AnalysisModalCloseBehavior,
} from '@/hooks/use-analysis-modal-route';
import { AnalysisConsumedTracker } from '@/components/analysis-consumed-tracker';
import { ArticleCardAnalysisClose } from '@/components/article-card/article-card-analysis/article-card-analysis-close';
import { ArticleCardAnalysisContent } from '@/components/article-card/article-card-analysis/article-card-analysis-content';

import { AnalysisContainerProvider } from './analysis-container';

interface ArticleAnalysisModalRouteProps {
  /** The article to analyse. Null only on a by-url landing whose article has
   *  no slug, which falls back to `articleId`. */
  slug: string | null;
  articleId?: number;
  /** Set by the `@modal` intercept (CRE-730 stacking guard). */
  suppressWhileInlineOpen?: boolean;
  /** `'back'` from the intercept, `'home'` (default) on a hard-load landing. */
  closeBehavior?: AnalysisModalCloseBehavior;
}

/** The open inline article modals, for the CRE-730 stacking guard. */
const inlineArticleModals = createInlineModalRegistry();

/**
 * The article analysis as a URL-addressed modal, the article counterpart of
 * `YtAnalysisModalRoute`: the `@modal` intercept mounts it over the page a
 * card was opened from, and the canonical / by-url pages mount it inline
 * over the news feed. Open state, close modes and the stacking guard come
 * from `useAnalysisModalRoute`.
 *
 * The surface is the analysis' size container: its `@cred-*` breakpoints
 * (300-500px, drawn for the extension's side panel) query the surface's
 * width, not the viewport's, and its tooltips portal into it so they size
 * the same way (`AnalysisContainerProvider`). The phone drawer is the
 * viewport's width; the desktop sheet steps through the tiers with the
 * window, at the widths the extension's side panel used.
 */
export const ArticleAnalysisModalRoute: React.FC<
  ArticleAnalysisModalRouteProps
> = ({
  slug,
  articleId,
  suppressWhileInlineOpen = false,
  closeBehavior = 'home',
}) => {
  const isMobile = useIsMobile();
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  // The consumption funnel's key: the slug, as `analysis_opened` reports it.
  const trackingSlug = slug ?? (articleId != null ? String(articleId) : null);

  const {
    shouldRender,
    dismissed,
    open,
    close,
    onOpenChange,
    onEscapeKeyDown,
    onPointerDownOutside,
  } = useAnalysisModalRoute({
    isAnalysisPath: isArticleAnalysisPath,
    closeBehavior,
    suppressWhileInlineOpen,
    registry: inlineArticleModals,
    isMobile,
  });

  const handleCloseButton = useCallback(() => close('close_button'), [close]);

  if (!shouldRender) return null;

  // Closed: unmount at once, the same as the video modal (see there).
  if (dismissed) return null;

  const content = (
    <AnalysisContainerProvider value={container}>
      {/* `analysis_consumed` / `article_consumed`: the reader scrolls this
          modal, not the feed behind it, so the scroll leg watches the modal's
          own scroller (the video modal mounts its tracker the same way). */}
      {trackingSlug && (
        <AnalysisConsumedTracker
          contentType="article"
          slug={trackingSlug}
          scrollContainer={scroller}
        />
      )}
      <ArticleCardAnalysisContent
        slug={slug}
        articleId={articleId}
        onClose={handleCloseButton}
      />
    </AnalysisContainerProvider>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        {/* z-200 (layout.z.modalOverlay) and the safe-area padding: the same
            700-767px rail band and installed-PWA notch fixes as the video
            drawer. No `space-y-*` here, unlike that drawer: tooltips portal
            into this surface, and as its last child they would push a margin
            onto the scroller (`mt-2` keeps the gap under the handle). */}
        <DrawerContent
          ref={setContainer}
          overlayClassName="z-200"
          className="@container z-200 size-full dark bg-black p-0 pt-[env(safe-area-inset-top)] pb-safe text-sidebar-foreground data-[vaul-drawer-direction=bottom]:max-h-dvh [&>div:first-child]:bg-[#c0c0c0]"
          onEscapeKeyDown={onEscapeKeyDown}
          onPointerDownOutside={onPointerDownOutside}
        >
          <div
            ref={setScroller}
            id="article-card-analysis-bottom-sheet"
            className="mt-2 flex-1 overflow-y-auto text-white overscroll-none"
          >
            <DrawerHeader className="sr-only bg-transparent">
              <DrawerTitle>Article Analysis</DrawerTitle>
              <DrawerDescription>
                Credibility, bias and claims analysis of this article.
              </DrawerDescription>
            </DrawerHeader>
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* z-200 = layout.z.modalOverlay (ux-bugs-003): above the app rail.
          The width steps up with the window (350px from 768, 380px from
          1024, 500px from 2500), and the @cred-* container tiers inside
          follow it. Every width is prefixed so it overrides the primitive's
          unprefixed `w-3/4` (tailwind-merge does not know the cred-*
          spacing, so it keeps both). */}
      <SheetContent
        ref={setContainer}
        side="right"
        overlayClassName="z-200 bg-black/30"
        className="@container z-200 flex flex-col border-none bg-black p-0 text-sidebar-foreground max-w-full sm:max-w-full min-[20rem]:w-cred-sm md:w-cred-md lg:w-cred-lg min-[156.25rem]:w-cred-xl [&>button]:hidden"
        onEscapeKeyDown={onEscapeKeyDown}
        onPointerDownOutside={onPointerDownOutside}
      >
        <AnimatePresence mode="wait">
          {open && <ArticleCardAnalysisClose onClose={handleCloseButton} />}
        </AnimatePresence>
        <SheetHeader className="sr-only">
          <SheetTitle>Article Analysis</SheetTitle>
          <SheetDescription>
            Credibility, bias and claims analysis of this article.
          </SheetDescription>
        </SheetHeader>
        <div
          ref={setScroller}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain text-white"
        >
          {content}
        </div>
      </SheetContent>
    </Sheet>
  );
};
